/**
 * Chat streaming event handler.
 *
 * Processes chat and agent event frames from the gateway.
 *
 * Chat events:
 * - delta: accumulated text (gateway sends full text so far; handler
 *          computes the incremental diff for downstream consumers)
 * - final: complete response (finalize message, include usage stats)
 * - aborted: request was aborted
 * - error: request failed
 *
 * Agent events:
 * - tool_call: agent is invoking a tool
 * - thinking: agent reasoning/thinking text
 *
 * Provides both a class-based `StreamingHandler` for stateful stream
 * management and standalone helper functions for simple use cases.
 */

import type { WSClient } from '../ws/client';
import type { EventFrame } from '../types/protocol';
import type {
  ChatEventPayload,
  ChatEventDelta,
  ChatEventFinal,
  ChatEventAborted,
  ChatEventError,
  ChatEventUserMessage,
  AgentEventPayload,
  ChatSideResultPayload,
  ShutdownPayload,
  PresencePayload,
  UpdateAvailablePayload,
  ExecApprovalRequestedPayload,
  ExecApprovalResolvedPayload,
  SessionsChangedPayload,
} from '../types/protocol';
import { normalizeContent } from '../utils/content';
import { getErrorMessage } from '../utils/errors';

// ── Callback Types ──

/** Called when a new stream starts (first delta received for a runId) */
export type StreamStartCallback = (runId: string, sessionKey: string) => void;

/** Called on each incremental text delta */
export type DeltaCallback = (
  runId: string,
  partialText: string,
  fullText: string,
  seq: number,
) => void;

/** Called when a stream completes with the final message */
export type CompleteCallback = (
  runId: string,
  message: string,
  usage: { inputTokens: number; outputTokens: number } | undefined,
  stopReason: string | undefined,
  sessionKey: string,
) => void;

/** Called when a stream is aborted */
export type AbortCallback = (runId: string) => void;

/** Called when a stream encounters an error */
export type ErrorCallback = (runId: string, errorMessage: string) => void;

/** Called when the agent invokes a tool */
export type ToolCallCallback = (
  runId: string,
  toolName: string,
  input: unknown,
  sessionKey: string | undefined,
) => void;

/** Called when a tool call completes (result received) */
export type ToolResultCallback = (
  runId: string,
  toolName: string,
  sessionKey: string | undefined,
) => void;

/** Called when the agent emits thinking/reasoning text */
export type ThinkingCallback = (runId: string, text: string, sessionKey: string | undefined) => void;

/** Called when context compaction occurs */
export type CompactionCallback = (
  runId: string,
  phase: 'start' | 'end',
  sessionKey: string | undefined,
) => void;

/** Called on agent lifecycle events */
export type LifecycleCallback = (
  runId: string,
  phase: 'start' | 'end' | 'error',
  sessionKey: string | undefined,
) => void;

/** Called when a user message arrives from another client (multi-client sync) */
export type UserMessageCallback = (
  runId: string,
  text: string,
  sessionKey: string,
  timestamp: number | undefined,
) => void;

/** Called when a tool call has a progress update */
export type ToolUpdateCallback = (
  runId: string,
  toolName: string,
  data: Record<string, unknown>,
  sessionKey: string | undefined,
) => void;

/** Called when a chat.side_result (BTW) arrives */
export type SideResultCallback = (
  payload: ChatSideResultPayload,
) => void;

/** Called when the gateway is shutting down */
export type ShutdownCallback = (
  payload: ShutdownPayload,
) => void;

/** Called when the presence list updates */
export type PresenceCallback = (
  payload: PresencePayload,
) => void;

/** Called when a gateway update is available */
export type UpdateAvailableCallback = (
  payload: UpdateAvailablePayload,
) => void;

/** Called when an exec command needs approval */
export type ExecApprovalRequestedCallback = (
  payload: ExecApprovalRequestedPayload,
) => void;

/** Called when an exec approval is resolved */
export type ExecApprovalResolvedCallback = (
  payload: ExecApprovalResolvedPayload,
) => void;

/** Called when a session lifecycle event occurs */
export type SessionsChangedCallback = (
  payload: SessionsChangedPayload,
) => void;

// ── Active Stream State ──

/** Internal state for a single active stream */
interface ActiveStream {
  /** Session key this stream belongs to */
  sessionKey: string;
  /** Accumulated full text from all deltas */
  fullText: string;
  /** Highest sequence number received */
  lastSeq: number;
}

// ── StreamingHandler Class ──

/**
 * Stateful handler for chat streaming events.
 *
 * Attaches to a WSClient's event system and manages active streams.
 * Tracks accumulated text per runId, handles sequence ordering,
 * and dispatches typed callbacks for each stream lifecycle event.
 *
 * @example
 * ```ts
 * const handler = new StreamingHandler(client);
 *
 * handler.onStreamStart((runId, sessionKey) => {
 *   console.log(`Stream started: ${runId}`);
 * });
 *
 * handler.onDelta((runId, partial, full, seq) => {
 *   updateUI(full); // full = accumulated text so far
 * });
 *
 * handler.onComplete((runId, message, usage, stopReason) => {
 *   finalizeMessage(message);
 * });
 *
 * handler.onError((runId, errorMessage) => {
 *   showError(errorMessage);
 * });
 *
 * // Clean up when done
 * handler.destroy();
 * ```
 */
export class StreamingHandler {
  private client: WSClient;
  private activeStreams = new Map<string, ActiveStream>();

  // Callback registrations (support multiple listeners per event)
  private streamStartCallbacks: StreamStartCallback[] = [];
  private deltaCallbacks: DeltaCallback[] = [];
  private completeCallbacks: CompleteCallback[] = [];
  private abortCallbacks: AbortCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private toolCallCallbacks: ToolCallCallback[] = [];
  private toolResultCallbacks: ToolResultCallback[] = [];
  private toolUpdateCallbacks: ToolUpdateCallback[] = [];
  private thinkingCallbacks: ThinkingCallback[] = [];
  private compactionCallbacks: CompactionCallback[] = [];
  private lifecycleCallbacks: LifecycleCallback[] = [];
  private userMessageCallbacks: UserMessageCallback[] = [];
  private sideResultCallbacks: SideResultCallback[] = [];
  private shutdownCallbacks: ShutdownCallback[] = [];
  private presenceCallbacks: PresenceCallback[] = [];
  private updateAvailableCallbacks: UpdateAvailableCallback[] = [];
  private execApprovalRequestedCallbacks: ExecApprovalRequestedCallback[] = [];
  private execApprovalResolvedCallbacks: ExecApprovalResolvedCallback[] = [];
  private sessionsChangedCallbacks: SessionsChangedCallback[] = [];

  // Bound handler references for clean removal
  private boundEventHandler: (frame: EventFrame) => void;

  /**
   * Create a new StreamingHandler and attach to the client.
   *
   * @param client - The WebSocket client to listen on
   */
  constructor(client: WSClient) {
    this.client = client;
    this.boundEventHandler = this.handleEvent.bind(this);
    this.client.on('event', this.boundEventHandler);
  }

  // ── Callback Registration ──

  /**
   * Register a callback for stream start events.
   * Fired when the first delta for a new runId is received.
   */
  onStreamStart(callback: StreamStartCallback): void {
    this.streamStartCallbacks.push(callback);
  }

  /**
   * Register a callback for delta events.
   * Called with both the incremental partial text and the accumulated full text.
   */
  onDelta(callback: DeltaCallback): void {
    this.deltaCallbacks.push(callback);
  }

  /**
   * Register a callback for stream completion.
   * Called with the final message text, token usage, and stop reason.
   */
  onComplete(callback: CompleteCallback): void {
    this.completeCallbacks.push(callback);
  }

  /**
   * Register a callback for stream abort events.
   */
  onAbort(callback: AbortCallback): void {
    this.abortCallbacks.push(callback);
  }

  /**
   * Register a callback for stream error events.
   */
  onError(callback: ErrorCallback): void {
    this.errorCallbacks.push(callback);
  }

  /**
   * Register a callback for agent tool call events.
   * Called when the agent invokes a tool during response generation.
   */
  onToolCall(callback: ToolCallCallback): void {
    this.toolCallCallbacks.push(callback);
  }

  /**
   * Register a callback for agent thinking events.
   * Called when the agent emits reasoning/thinking text.
   */
  onThinking(callback: ThinkingCallback): void {
    this.thinkingCallbacks.push(callback);
  }

  /**
   * Register a callback for tool result events.
   * Called when a tool call completes with a result.
   */
  onToolResult(callback: ToolResultCallback): void {
    this.toolResultCallbacks.push(callback);
  }

  /**
   * Register a callback for compaction events.
   * Called when the gateway compacts the context window.
   */
  onCompaction(callback: CompactionCallback): void {
    this.compactionCallbacks.push(callback);
  }

  /**
   * Register a callback for lifecycle events.
   * Called on run start/end/error.
   */
  onLifecycle(callback: LifecycleCallback): void {
    this.lifecycleCallbacks.push(callback);
  }

  /**
   * Register a callback for user message events (multi-client sync).
   * Called when another client on the same session sends a message.
   */
  onUserMessage(callback: UserMessageCallback): void {
    this.userMessageCallbacks.push(callback);
  }

  /**
   * Register a callback for tool progress updates.
   * Called when a tool emits a phase:"update" event with partial progress.
   */
  onToolUpdate(callback: ToolUpdateCallback): void {
    this.toolUpdateCallbacks.push(callback);
  }

  /**
   * Register a callback for chat.side_result (BTW) events.
   * Called when the gateway provides a "by the way" response alongside the main reply.
   */
  onSideResult(callback: SideResultCallback): void {
    this.sideResultCallbacks.push(callback);
  }

  /**
   * Register a callback for shutdown events.
   * Called when the gateway is shutting down.
   */
  onShutdown(callback: ShutdownCallback): void {
    this.shutdownCallbacks.push(callback);
  }

  /**
   * Register a callback for presence events.
   * Called when the connected clients/devices list changes.
   */
  onPresence(callback: PresenceCallback): void {
    this.presenceCallbacks.push(callback);
  }

  /**
   * Register a callback for update.available events.
   * Called when a new gateway version is available.
   */
  onUpdateAvailable(callback: UpdateAvailableCallback): void {
    this.updateAvailableCallbacks.push(callback);
  }

  /**
   * Register a callback for exec.approval.requested events.
   * Called when an agent command needs operator approval.
   */
  onExecApprovalRequested(callback: ExecApprovalRequestedCallback): void {
    this.execApprovalRequestedCallbacks.push(callback);
  }

  /**
   * Register a callback for exec.approval.resolved events.
   * Called when an exec approval decision has been made.
   */
  onExecApprovalResolved(callback: ExecApprovalResolvedCallback): void {
    this.execApprovalResolvedCallbacks.push(callback);
  }

  /**
   * Register a callback for sessions.changed events.
   * Called on session lifecycle changes (start/end/error/message).
   */
  onSessionsChanged(callback: SessionsChangedCallback): void {
    this.sessionsChangedCallbacks.push(callback);
  }

  // ── State Queries ──

  /**
   * Get the accumulated text for an active stream.
   * Returns undefined if the stream is not active.
   */
  getStreamText(runId: string): string | undefined {
    return this.activeStreams.get(runId)?.fullText;
  }

  /**
   * Check if a specific stream is currently active.
   */
  isStreamActive(runId: string): boolean {
    return this.activeStreams.has(runId);
  }

  /**
   * Get all currently active stream run IDs.
   */
  getActiveRunIds(): string[] {
    return Array.from(this.activeStreams.keys());
  }

  // ── Lifecycle ──

  /**
   * Remove all event listeners and clear state.
   * Call this when the handler is no longer needed.
   */
  destroy(): void {
    this.client.off('event', this.boundEventHandler);
    this.activeStreams.clear();
    this.streamStartCallbacks = [];
    this.deltaCallbacks = [];
    this.completeCallbacks = [];
    this.abortCallbacks = [];
    this.errorCallbacks = [];
    this.toolCallCallbacks = [];
    this.toolResultCallbacks = [];
    this.toolUpdateCallbacks = [];
    this.thinkingCallbacks = [];
    this.compactionCallbacks = [];
    this.lifecycleCallbacks = [];
    this.userMessageCallbacks = [];
    this.sideResultCallbacks = [];
    this.shutdownCallbacks = [];
    this.presenceCallbacks = [];
    this.updateAvailableCallbacks = [];
    this.execApprovalRequestedCallbacks = [];
    this.execApprovalResolvedCallbacks = [];
    this.sessionsChangedCallbacks = [];
  }

  /**
   * Clear all active stream state without removing listeners.
   * Useful on reconnection to reset stale state.
   */
  reset(): void {
    this.activeStreams.clear();
  }

  // ── Event Dispatching ──

  /** Main event handler — routes all event frames by event name */
  private handleEvent(frame: EventFrame): void {
    switch (frame.event) {
      case 'chat':
        this.handleChatEvent(frame.payload as ChatEventPayload);
        break;
      case 'agent':
        this.handleAgentEvent(frame.payload as AgentEventPayload);
        break;
      case 'chat.side_result':
        this.dispatchCallbacks(this.sideResultCallbacks, frame.payload as ChatSideResultPayload);
        break;
      case 'shutdown':
        this.dispatchCallbacks(this.shutdownCallbacks, frame.payload as ShutdownPayload);
        break;
      case 'presence':
        this.dispatchCallbacks(this.presenceCallbacks, frame.payload as PresencePayload);
        break;
      case 'update.available':
        this.dispatchCallbacks(this.updateAvailableCallbacks, frame.payload as UpdateAvailablePayload);
        break;
      case 'exec.approval.requested':
        this.dispatchCallbacks(this.execApprovalRequestedCallbacks, frame.payload as ExecApprovalRequestedPayload);
        break;
      case 'exec.approval.resolved':
        this.dispatchCallbacks(this.execApprovalResolvedCallbacks, frame.payload as ExecApprovalResolvedPayload);
        break;
      case 'sessions.changed':
        this.dispatchCallbacks(this.sessionsChangedCallbacks, frame.payload as SessionsChangedPayload);
        break;
      // Explicitly ignored events (not relevant to voice app):
      // tick (handled in WSClient), health, heartbeat, cron,
      // talk.mode, voicewake.changed, node.*, device.*, file.uploaded, session.message, session.tool
      default:
        break;
    }
  }

  /** Generic callback dispatcher — invokes all registered callbacks with error protection */
  private dispatchCallbacks<T>(callbacks: Array<(payload: T) => void>, payload: T): void {
    for (const cb of callbacks) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[streaming] Error in event callback:', e);
      }
    }
  }

  /** Handle chat.event payloads by state */
  private handleChatEvent(payload: ChatEventPayload): void {
    switch (payload.state) {
      case 'delta':
        this.handleDelta(payload);
        break;
      case 'final':
        this.handleFinal(payload);
        break;
      case 'aborted':
        this.handleAborted(payload);
        break;
      case 'error':
        this.handleError(payload);
        break;
      case 'user_message':
        this.handleUserMessage(payload as ChatEventUserMessage);
        break;
      default:
        console.warn(
          '[streaming] Unknown chat.event state:',
          (payload as Record<string, unknown>).state,
        );
    }
  }

  /**
   * Handle agent.event payloads by stream type.
   *
   * The gateway sends `{ stream: "...", data: {...}, runId, sessionKey }`.
   */
  private handleAgentEvent(payload: AgentEventPayload): void {
    const { runId, stream, data, sessionKey } = payload;

    switch (stream) {
      case 'tool': {
        const toolName = typeof data?.name === 'string' ? data.name : 'unknown';
        const phase = typeof data?.phase === 'string' ? data.phase : undefined;

        if (phase === 'result') {
          // Tool completed — phase:"result" carries meta, isError (result stripped by default)
          for (const cb of this.toolResultCallbacks) {
            try {
              cb(runId, toolName, sessionKey);
            } catch (e) {
              console.error('[streaming] Error in toolResult callback:', e);
            }
          }
        } else if (phase === 'start') {
          // Tool invocation started — phase:"start" carries args
          const args = data?.args;
          for (const cb of this.toolCallCallbacks) {
            try {
              cb(runId, toolName, args, sessionKey);
            } catch (e) {
              console.error('[streaming] Error in toolCall callback:', e);
            }
          }
        }
        if (phase === 'update') {
          // Tool progress update — forward to listeners
          for (const cb of this.toolUpdateCallbacks) {
            try {
              cb(runId, toolName, data ?? {}, sessionKey);
            } catch (e) {
              console.error('[streaming] Error in toolUpdate callback:', e);
            }
          }
        }
        break;
      }

      case 'thinking': {
        const text = typeof data?.text === 'string'
          ? data.text
          : typeof data?.thinking === 'string'
            ? data.thinking
            : '';
        if (text) {
          for (const cb of this.thinkingCallbacks) {
            try {
              cb(runId, text, sessionKey);
            } catch (e) {
              console.error('[streaming] Error in thinking callback:', e);
            }
          }
        }
        break;
      }

      case 'compaction': {
        const phase = typeof data?.phase === 'string' ? data.phase as 'start' | 'end' : null;
        if (phase) {
          for (const cb of this.compactionCallbacks) {
            try {
              cb(runId, phase, sessionKey);
            } catch (e) {
              console.error('[streaming] Error in compaction callback:', e);
            }
          }
        }
        break;
      }

      case 'lifecycle': {
        const phase = typeof data?.phase === 'string' ? data.phase as 'start' | 'end' | 'error' : null;
        if (phase) {
          for (const cb of this.lifecycleCallbacks) {
            try {
              cb(runId, phase, sessionKey);
            } catch (e) {
              console.error('[streaming] Error in lifecycle callback:', e);
            }
          }
        }
        break;
      }

      case 'assistant':
        // Text deltas are handled via the chat event path (emitChatDelta on
        // the server side). We can safely ignore assistant stream events here.
        break;

      case 'error':
        // Sequence gap errors from the gateway — log but don't crash
        console.warn('[streaming] Agent stream error:', data);
        break;

      default:
        // Silently ignore unknown agent event streams — extensibility point
        break;
    }
  }

  // ── Chat Event Handlers ──

  /** Handle delta: accumulate text, fire callbacks */
  private handleDelta(delta: ChatEventDelta): void {
    const { runId, sessionKey, seq } = delta;
    const incomingText = normalizeContent(delta.message?.content ?? '');

    let stream = this.activeStreams.get(runId);

    // First delta for this runId — start a new stream
    if (!stream) {
      stream = {
        sessionKey,
        fullText: '',
        lastSeq: -1,
      };
      this.activeStreams.set(runId, stream);

      // Notify stream start
      for (const cb of this.streamStartCallbacks) {
        try {
          cb(runId, sessionKey);
        } catch (e) {
          console.error('[streaming] Error in streamStart callback:', e);
        }
      }
    }

    // Sequence ordering: only process if this is the next expected seq
    // or a later one (gateway guarantees ordering on a single connection,
    // but we guard against duplicates)
    if (seq <= stream.lastSeq) {
      return; // Duplicate or out-of-order delta, skip
    }

    stream.lastSeq = seq;

    // The gateway sends the full accumulated text in each delta,
    // not incremental chunks. Compute the actual new text (delta)
    // by diffing against what we already have.
    const previousText = stream.fullText;
    let actualDelta: string;

    if (incomingText.startsWith(previousText)) {
      // Normal case: incoming text extends what we have
      actualDelta = incomingText.substring(previousText.length);
      stream.fullText = incomingText;
    } else {
      // Edge case: text was replaced/corrected — treat as full replacement.
      // Pass the entire new text as the delta so consumers can append it,
      // but reset fullText to avoid stale state.
      actualDelta = incomingText;
      stream.fullText = incomingText;
    }

    // Notify delta listeners with the incremental delta and full text
    for (const cb of this.deltaCallbacks) {
      try {
        cb(runId, actualDelta, stream.fullText, seq);
      } catch (e) {
        console.error('[streaming] Error in delta callback:', e);
      }
    }
  }

  /** Handle final: emit complete message, clean up stream */
  private handleFinal(final: ChatEventFinal): void {
    const { runId, sessionKey, usage, stopReason } = final;
    const finalContent = normalizeContent(final.message?.content ?? '');

    // Resolve sessionKey: prefer the tracked stream's sessionKey (set during
    // onStreamStart), fall back to the event payload's sessionKey for finals
    // that arrive without a preceding delta (e.g. background/injected messages).
    const resolvedSessionKey =
      this.activeStreams.get(runId)?.sessionKey ?? sessionKey;

    // Clean up the active stream
    this.activeStreams.delete(runId);

    // Notify completion
    for (const cb of this.completeCallbacks) {
      try {
        cb(runId, finalContent, usage, stopReason, resolvedSessionKey);
      } catch (e) {
        console.error('[streaming] Error in complete callback:', e);
      }
    }
  }

  /** Handle aborted: clean up and notify */
  private handleAborted(aborted: ChatEventAborted): void {
    const { runId } = aborted;

    this.activeStreams.delete(runId);

    for (const cb of this.abortCallbacks) {
      try {
        cb(runId);
      } catch (e) {
        console.error('[streaming] Error in abort callback:', e);
      }
    }
  }

  /** Handle error: clean up and notify */
  private handleError(error: ChatEventError): void {
    const { runId, errorMessage } = error;
    const normalizedErrorMessage = getErrorMessage(errorMessage);

    this.activeStreams.delete(runId);

    for (const cb of this.errorCallbacks) {
      try {
        cb(runId, normalizedErrorMessage);
      } catch (e) {
        console.error('[streaming] Error in error callback:', e);
      }
    }
  }

  /** Handle user_message: notify listeners for multi-client sync */
  private handleUserMessage(payload: ChatEventUserMessage): void {
    const { runId, sessionKey, message } = payload;
    const text = normalizeContent(message?.content ?? '');
    const timestamp = message?.timestamp;

    for (const cb of this.userMessageCallbacks) {
      try {
        cb(runId, text, sessionKey, timestamp);
      } catch (e) {
        console.error('[streaming] Error in userMessage callback:', e);
      }
    }
  }
}

// ── Standalone Helper Functions ──

/** Simple callbacks for the standalone handler factory */
export interface StreamingCallbacks {
  onDelta: (delta: ChatEventDelta) => void;
  onFinal: (final: ChatEventFinal) => void;
  onAborted: (aborted: ChatEventAborted) => void;
  onError: (error: ChatEventError) => void;
}

/**
 * Create a simple chat.event handler function.
 *
 * Returns a function that can be registered with `WSClient.on('event', handler)`.
 * Filters and dispatches `chat.event` frames to the appropriate callback.
 *
 * For stateful stream management (text accumulation, seq tracking),
 * use {@link StreamingHandler} instead.
 *
 * @param callbacks - Object with handler functions for each event state
 * @returns An event handler function for WSClient
 *
 * @example
 * ```ts
 * const handler = createStreamingHandler({
 *   onDelta: (d) => appendText(d.message.content),
 *   onFinal: (f) => finalize(f.message.content),
 *   onAborted: (a) => cleanup(a.runId),
 *   onError: (e) => showError(e.errorMessage),
 * });
 * client.on('event', handler);
 * ```
 */
export function createStreamingHandler(
  callbacks: StreamingCallbacks,
): (frame: EventFrame) => void {
  return (frame: EventFrame) => {
    if (frame.event !== 'chat') return;

    const payload = frame.payload as ChatEventPayload;

    switch (payload.state) {
      case 'delta':
        callbacks.onDelta(payload);
        break;
      case 'final':
        callbacks.onFinal(payload);
        break;
      case 'aborted':
        callbacks.onAborted(payload);
        break;
      case 'error':
        callbacks.onError(payload);
        break;
      case 'user_message':
        // Handled by StreamingHandler.onUserMessage; standalone handler ignores it
        break;
      default:
        console.warn(
          '[streaming] Unknown chat.event state:',
          (payload as Record<string, unknown>).state,
        );
    }
  };
}

// ── Type Guards ──

/**
 * Check if a chat.event payload is a delta (for type narrowing).
 */
export function isChatDelta(
  payload: ChatEventPayload,
): payload is ChatEventDelta {
  return payload.state === 'delta';
}

/**
 * Check if a chat.event payload is a final (for type narrowing).
 */
export function isChatFinal(
  payload: ChatEventPayload,
): payload is ChatEventFinal {
  return payload.state === 'final';
}

/**
 * Check if a chat.event indicates the stream has ended.
 * Returns true for final, aborted, or error states.
 */
export function isStreamEnd(payload: ChatEventPayload): boolean {
  return (
    payload.state === 'final' ||
    payload.state === 'aborted' ||
    payload.state === 'error'
  );
}
